import {useMemo} from 'react'
import {useTranslation} from 'react-i18next'
import {LienzoSvg, corto} from '@/components/analisis/lienzo-svg'

/**
 * Solo lectura, derivado de `parent_causa_id`; el backend dibuja el mismo árbol para el informe 1401.
 * Determinista: cada hoja toma un slot en orden DFS y cada padre se centra sobre sus hijos dibujados.
 */
export type CausaArbol = {id: string; parent_causa_id: string | null; descripcion: string}

type CajaArbol = {
  key: string
  tipo: 'evento' | 'causa' | 'mas'
  nivel: number
  x: number
  y: number
  ancho: number
  texto: string
  completo: string
  /** Solo en las cajas «+N más»: cuántas causas cuenta el pliegue. */
  plegadas: number
}

type AristaArbol = {key: string; d: string}

export type PlanoArbol = {cajas: CajaArbol[]; aristas: AristaArbol[]; ancho: number; alto: number}

// Gemelo de `backend/app/core/arbol_png.py`: si estos números divergen, papel y pantalla pliegan distinto.
const MAX_HIJOS_DIBUJADOS = 4
const NIVEL_MAXIMO = 3
const MAX_CARACTERES_EVENTO = 60
const ANCHO_CAJA = 210
const ANCHO_CAJA_EVENTO = 430
const ALTO_CAJA = 58
const ANCHO_COLUMNA = 234
const ALTO_FILA = 108
const MARGEN = 16

type Nodo = {id: string; texto: string; hijos: Nodo[]}

function armar(causas: CausaArbol[]): Nodo[] {
  const vivos = new Set(causas.map((c) => c.id))
  const porPadre = new Map<string, CausaArbol[]>()
  for (const c of causas) {
    // Un padre ausente cuelga la causa de la raíz: el dibujo nunca pierde una causa aunque el grafo llegue incompleto.
    const padre = c.parent_causa_id && vivos.has(c.parent_causa_id) ? c.parent_causa_id : ''
    porPadre.set(padre, [...(porPadre.get(padre) ?? []), c])
  }
  const hijosDe = (id: string): Nodo[] =>
    (porPadre.get(id) ?? []).map((c) => ({id: c.id, texto: c.descripcion, hijos: hijosDe(c.id)}))
  return hijosDe('')
}

function xDeSlot(slot: number) {
  return MARGEN + ANCHO_CAJA / 2 + slot * ANCHO_COLUMNA
}

function yDeNivel(nivel: number) {
  return MARGEN + ALTO_CAJA / 2 + nivel * ALTO_FILA
}

function codo(padre: CajaArbol, hijo: CajaArbol) {
  const desde = padre.y + ALTO_CAJA / 2
  const hasta = hijo.y - ALTO_CAJA / 2
  const medio = (desde + hasta) / 2
  return `M ${padre.x} ${desde} V ${medio} H ${hijo.x} V ${hasta}`
}

export function disponerArbol(causas: CausaArbol[], problema: string): PlanoArbol | null {
  const raices = armar(causas)
  if (raices.length === 0) return null

  const cajas: CajaArbol[] = []
  const aristas: AristaArbol[] = []
  let slots = 0

  function ubicar(nodo: Nodo, nivel: number): CajaArbol {
    // Misma regla de pliegue que `arbol_png.py::_camada`: lo que sobra de ancho o de hondura cae en «+N más».
    const visibles = nivel < NIVEL_MAXIMO ? nodo.hijos.slice(0, MAX_HIJOS_DIBUJADOS) : []
    const debajo = visibles.map((hijo) => ubicar(hijo, nivel + 1))
    const sobran = nodo.hijos.length - debajo.length
    if (sobran > 0) {
      debajo.push({
        key: `${nodo.id}-mas`,
        tipo: 'mas',
        nivel: nivel + 1,
        x: xDeSlot(slots++),
        y: yDeNivel(nivel + 1),
        ancho: ANCHO_CAJA,
        texto: '',
        completo: '',
        plegadas: sobran,
      })
    }
    const caja: CajaArbol = {
      key: nodo.id,
      tipo: 'causa',
      nivel,
      x: debajo.length > 0 ? debajo.reduce((suma, h) => suma + h.x, 0) / debajo.length : xDeSlot(slots++),
      y: yDeNivel(nivel),
      ancho: ANCHO_CAJA,
      texto: corto(nodo.texto),
      completo: nodo.texto,
      plegadas: 0,
    }
    cajas.push(caja, ...debajo.filter((h) => h.tipo === 'mas'))
    for (const hijo of debajo) aristas.push({key: `${caja.key}-${hijo.key}`, d: codo(caja, hijo)})
    return caja
  }

  const arriba = raices.map((raiz) => ubicar(raiz, 1))
  const ancho = MARGEN * 2 + ANCHO_CAJA + (slots - 1) * ANCHO_COLUMNA
  // El informe le da 60 caracteres al evento; la caja se encoge si el árbol es más angosto.
  const anchoEvento = Math.min(ANCHO_CAJA_EVENTO, ancho - MARGEN * 2)
  const centro = arriba.reduce((suma, h) => suma + h.x, 0) / arriba.length
  const evento: CajaArbol = {
    key: 'evento',
    tipo: 'evento',
    nivel: 0,
    x: Math.min(Math.max(centro, MARGEN + anchoEvento / 2), ancho - MARGEN - anchoEvento / 2),
    y: yDeNivel(0),
    ancho: anchoEvento,
    texto: corto(problema, MAX_CARACTERES_EVENTO),
    completo: problema,
    plegadas: 0,
  }
  cajas.push(evento)
  for (const hijo of arriba) aristas.push({key: `evento-${hijo.key}`, d: codo(evento, hijo)})

  cajas.sort((a, b) => a.nivel - b.nivel || a.x - b.x)
  const niveles = Math.max(...cajas.map((c) => c.nivel))
  return {cajas, aristas, ancho, alto: MARGEN * 2 + ALTO_CAJA + niveles * ALTO_FILA}
}

export function ArbolDeCausas({causas, problema}: {causas: CausaArbol[]; problema: string}) {
  const {t} = useTranslation()
  const plano = useMemo(() => disponerArbol(causas, problema), [causas, problema])
  if (!plano) return null

  return (
    <LienzoSvg
      testId="arbol-de-causas"
      ariaLabel={t('investigacion.causas.arbolAria', {problema})}
      ancho={plano.ancho}
      alto={plano.alto}
      anchoMinimo={Math.min(plano.ancho, 760)}>
      {plano.aristas.map((arista) => (
        <path
          key={arista.key}
          d={arista.d}
          fill="none"
          stroke="currentColor"
          strokeWidth={1.6}
          className="text-primary/40"
        />
      ))}
      {plano.cajas.map((caja) => (
        <g key={caja.key} data-testid={`arbol-caja-${caja.tipo}`}>
          <rect
            x={caja.x - caja.ancho / 2}
            y={caja.y - ALTO_CAJA / 2}
            width={caja.ancho}
            height={ALTO_CAJA}
            rx={10}
            fill="currentColor"
            stroke="currentColor"
            strokeWidth={1.2}
            className={caja.tipo === 'evento' ? 'fill-primary text-primary' : 'fill-card text-border'}
          />
          {caja.completo && <title>{caja.completo}</title>}
          <text
            x={caja.x}
            y={caja.y + 4}
            textAnchor="middle"
            fill="currentColor"
            className={
              caja.tipo === 'evento'
                ? 'fill-primary-foreground text-[12px] font-semibold'
                : 'fill-foreground text-[12px]'
            }>
            {caja.tipo === 'mas' ? t('analisis.espina.masCausas', {count: caja.plegadas}) : caja.texto}
          </text>
        </g>
      ))}
    </LienzoSvg>
  )
}
